import React, { useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import './checkInComponents.css';

/* Keys used when WRITING edits back through onChange */
const FIELDS = {
  progressStatus: 'progressStatus',
  percentComplete: 'percentComplete',
  employeeComments: 'employeeComments', 
  supportNeeds: 'supportNeeds',
  supportCategory: 'supportCategory',
  supportPriority: 'supportPriority',   
  managerComments: 'managerComments',
  nextSteps: 'nextSteps',
};

/* dataElement IDs — same ones CheckInDashboard uses */
const DE = {
  name: 'EuN5ztFnXsQ',
  weight: 'yfb9RCRpMB2',
  startDate: 'eDcVy661j9e',
  endDate: 'J0rjxrDKn1l',
};

/* ---------- Resolvers: read from mapped keys OR raw dataValues ---------- */
const pickFirst = (source, keys) => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const getDataValue = (source, dataElementId) => {
  if (!Array.isArray(source?.dataValues)) return undefined;
  return source.dataValues.find((dv) => dv.dataElement === dataElementId)?.value || undefined;
};

const formatDate = (value) => {
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d.toLocaleDateString();
};

const PROGRESS_OPTIONS = [
  { label: 'Not Started', code: 'Not_started' }, 
  { label: 'On Track', code: 'On_track' },       
  { label: 'At Risk', code: 'At_risk' },         
  { label: 'Behind', code: 'behind' },           
  { label: 'Completed', code: 'completed' }      
];

const CATEGORY_OPTIONS = [
  { label: 'Training', code: 'Training' },
  { label: 'Equipment', code: 'Equipment' },
  { label: 'Mentoring', code: 'Mentoring' },
  { label: 'Resources', code: 'Resources' },
  { label: 'Process Improvement', code: 'Process Improvement' }, 
  { label: 'Other', code: 'Other' }
];

const PRIORITY_OPTIONS = [
  { label: 'Critical', code: 'critical' }, 
  { label: 'High', code: 'high' },         
  { label: 'Medium', code: 'medium' },     
  { label: 'Low', code: 'low' }            
];

const ObjectiveCard = ({ objective, index, isExpanded, onToggleExpand, onChange, isReadOnly, isManagerView, managerCanEdit, status }) => {
  const rootRef = useRef(null);
  const headRef = useRef(null);

  const id = objective.id || objective.event;
  const name =
    pickFirst(objective, ['text', 'name', 'objectiveName', 'title', 'objectiveTitle', 'businessObjective', 'objective']) ||
    getDataValue(objective, DE.name) ||
    `Objective ${index + 1}`;
  const description = pickFirst(objective, ['description', 'objectiveDescription', 'details', 'tasks']);
  const kra = pickFirst(objective, ['kra', 'keyResultArea']);
  const kpi = pickFirst(objective, ['kpi', 'keyPerformanceIndicator']);
  const weight = pickFirst(objective, ['weight']) || getDataValue(objective, DE.weight);
  const startDate = formatDate(pickFirst(objective, ['startDate', 'start']) || getDataValue(objective, DE.startDate));
  const endDate = formatDate(pickFirst(objective, ['endDate', 'end']) || getDataValue(objective, DE.endDate));

  const percent = Math.min(100, Math.max(0, Number(objective[FIELDS.percentComplete]) || 0));

  const employeeLocked = isReadOnly || isManagerView;                 
  const managerLocked = managerCanEdit === undefined ? !isManagerView : !managerCanEdit;
  const set = (field, value) => onChange(id, field, value);
  const showManagerSection = isManagerView || 
                             ['REVIEWED', 'MEETING_SCHEDULED', 'COMPLETED'].includes(status) || 
                             objective.managerComments || 
                             objective.nextSteps;

  useEffect(() => {
    if (!isExpanded) return;
    const timer = setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      headRef.current?.focus({ preventScroll: true });
    }, 60);
    return () => clearTimeout(timer);
  }, [isExpanded]);

  return (
    <div ref={rootRef} id={`objective-${id}`} className={`cc-obj ${isExpanded ? 'cc-obj--open' : ''}`}>
      <button
        ref={headRef}
        type="button"
        className="cc-obj-head"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-controls={`objective-body-${id}`}
      >
        <span className="cc-obj-index">#{index + 1}</span>
        <span className="cc-obj-headtext">
          <span className="cc-obj-title">{name}</span>
          {description && <span className="cc-obj-desc">{description}</span>}
          <span className="cc-obj-meta">
            {kra && <span>KPA: {kra}</span>}
            {kpi && <span>KPI: {kpi}</span>}
            {weight && <span>Weight: {weight}%</span>}
            {startDate && <span>Start: {startDate}</span>}
            {endDate && <span>End: {endDate}</span>}
          </span>
        </span>
        <span className="cc-obj-right">
          <span className="cc-obj-pct">{percent}%</span>
          <ChevronDown className={`cc-chevron ${isExpanded ? 'cc-chevron--open' : ''}`} size={18} />
        </span>
      </button>

      {isExpanded && (
        <div className="cc-obj-body" id={`objective-body-${id}`}>
          <div className="cc-grid-2">
            <div className="cc-field">
              <label className="cc-label">Progress Status</label>
              <select
                className="cc-select"
                value={objective[FIELDS.progressStatus] || ''}
                disabled={employeeLocked}
                onChange={(e) => set(FIELDS.progressStatus, e.target.value)}
              >
                <option value="">Select Progress</option>
                {PROGRESS_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option> 
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">
                Percent Complete <span className="cc-label-value">{percent}%</span>
              </label>
              <input
                type="range"
                min="0" max="100" step="5"
                className="cc-range"
                style={{ '--fill': `${percent}%` }}
                value={percent}
                disabled={employeeLocked}
                onChange={(e) => set(FIELDS.percentComplete, Number(e.target.value))}
              />
            </div>
          </div>

          <div className="cc-field">
            <label className="cc-label">Your Comments</label>
            <textarea
              className="cc-textarea"
              placeholder="Provide your self-assessment comments..."
              value={objective[FIELDS.employeeComments] || ''} 
              disabled={employeeLocked}
              onChange={(e) => set(FIELDS.employeeComments, e.target.value)} 
            />
          </div>

          <div className="cc-section">
            <h5>Performance Support Needs</h5>
            <div className="cc-field">
              <label className="cc-label">What support do you need to achieve this objective?</label>
              <textarea
                className="cc-textarea"
                placeholder="E.g., Training on new software, additional staff, equipment..."
                value={objective[FIELDS.supportNeeds] || ''}
                disabled={employeeLocked}
                onChange={(e) => set(FIELDS.supportNeeds, e.target.value)}
              />
            </div>
            <div className="cc-grid-2">
              <div className="cc-field">
                <label className="cc-label">Support Category</label>
                <select
                  className="cc-select"
                  value={objective[FIELDS.supportCategory] || ''}
                  disabled={employeeLocked}
                  onChange={(e) => set(FIELDS.supportCategory, e.target.value)}
                >
                  <option value="">Select Category</option>
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option> 
                  ))}
                </select>
              </div>
              <div className="cc-field">
                <label className="cc-label">Priority</label>
                <select
                  className="cc-select"
                  value={objective[FIELDS.supportPriority] || ''} 
                  disabled={employeeLocked}
                  onChange={(e) => set(FIELDS.supportPriority, e.target.value)} 
                >
                  <option value="">Select Priority</option>
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option> 
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ✅ Hide Manager Section for Staff UNLESS the manager has already left feedback */}
          {showManagerSection && (
            <div className="cc-section cc-section--manager">
              <h5>Manager Feedback</h5>
              <div className="cc-field">
                <label className="cc-label">Manager's Comments</label>
                <textarea
                  className="cc-textarea"
                  placeholder={managerLocked ? "Waiting for manager's comments..." : 'Share your feedback on this objective...'}
                  value={objective[FIELDS.managerComments] || ''}
                  disabled={managerLocked}
                  onChange={(e) => set(FIELDS.managerComments, e.target.value)}
                />
              </div>
              <div className="cc-field">
                <label className="cc-label">Next Steps / Action Items</label>
                <textarea
                  className="cc-textarea"
                  placeholder={managerLocked ? "Waiting for manager's action items..." : 'Define action items and next steps...'}
                  value={objective[FIELDS.nextSteps] || ''}
                  disabled={managerLocked}
                  onChange={(e) => set(FIELDS.nextSteps, e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ObjectiveCard;